import { useEffect, useState } from 'react'
import { formatMoney } from '../lib/format.js'
import CateringFollowupSection from './CateringFollowupSection.jsx'
import CateringProposalSection from './CateringProposalSection.jsx'
import CateringImpactSection from './CateringImpactSection.jsx'

// Modal for creating or editing a catering inquiry.
// Three modes:
//   1. Create (no inquiry passed) — blank form
//   2. Edit (inquiry passed) — pre-filled
//   3. Edit + delete enabled (so manager can remove a wrong-entry)
//
// Status is editable here too, so the workflow runs through this modal:
// create as 'new' → flip to 'quoted' once you've sent a quote → flip to
// 'confirmed' on customer agreement → 'completed' after the event.
const STATUS_OPTIONS = [
  { value: 'new',       label: 'New' },
  { value: 'quoted',    label: 'Quoted' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'declined',  label: 'Declined' },
]

export default function CateringInquiryModal({
  open,
  onClose,
  onSave,           // (id, patch) → Promise   (edit mode)
  onCreate,         // (payload) → Promise<row> (create mode)
  onDelete,         // (id) → Promise          (optional, edit mode)
  inquiry,          // optional; null = create mode
  onJumpToSchedule, // optional (date) => void — deep-link from impact view
  units,            // optional [{id,name}] — group-create mode only
  onSuggestUnit,    // optional (fields) => Promise<suggestion> — group-create mode only
}) {
  const isCreateMode = !inquiry
  // Group-create assignment mode is active ONLY when this modal is in
  // create mode AND the caller passed a units list + an onSuggestUnit
  // callback. Edit mode and single-unit create NEVER enter this path, so
  // their behavior is byte-identical to before this ship.
  const assignmentMode = isCreateMode && Array.isArray(units) && units.length > 0 && typeof onSuggestUnit === 'function'

  const [form, setForm] = useState(() => emptyForm())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Unit-assignment state (group-create mode only). The suggest call has
  // its OWN in-flight flag (suggesting) so it never collides with the
  // submit lifecycle — submitting still resets only in handleSubmit's
  // finally. assignedUnitId is the editable, operator-overridable choice.
  const [assignedUnitId, setAssignedUnitId] = useState('')
  const [suggesting, setSuggesting] = useState(false)
  const [suggestion, setSuggestion] = useState(null) // { suggested_unit_id, confidence, reasoning, ... }
  const [suggestRequested, setSuggestRequested] = useState(false) // single-shot guard
  const [showWhy, setShowWhy] = useState(false)

  // Hydrate from inquiry on open.
  useEffect(() => {
    if (!open) return
    if (inquiry) {
      setForm({
        contact_name:    inquiry.contact_name    ?? '',
        contact_email:   inquiry.contact_email   ?? '',
        contact_phone:   inquiry.contact_phone   ?? '',
        event_date:      inquiry.event_date      ?? '',
        event_time:      (inquiry.event_time ?? '').slice(0, 5),  // HH:MM
        headcount:       inquiry.headcount  != null ? String(inquiry.headcount)  : '',
        location:        inquiry.location        ?? '',
        event_type:      inquiry.event_type      ?? '',
        notes:           inquiry.notes           ?? '',
        estimated_total: inquiry.estimated_total != null ? String(inquiry.estimated_total) : '',
        status:          inquiry.status          ?? 'new',
      })
    } else {
      setForm(emptyForm())
    }
    setError(null)
    // Reset the unit-assignment state every time the modal opens. The
    // modal stays mounted between opens, so stale suggestion/selection
    // must clear or the next group-create would show the last event's
    // pre-selection.
    setAssignedUnitId('')
    setSuggestion(null)
    setSuggesting(false)
    setSuggestRequested(false)
    setShowWhy(false)
  }, [open, inquiry])

  if (!open) return null

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  // Fire the AI unit-suggestion ONCE per open, after the key fields are
  // filled. Wired to on-blur of event_type / event_date / headcount via
  // maybeSuggestUnit. The suggesting flag is a single in-flight guard,
  // independent of submitting (modal-lock house rule). NEVER runs in edit
  // mode or single-unit create (assignmentMode is false there).
  async function maybeSuggestUnit() {
    if (!assignmentMode) return
    if (suggestRequested || suggesting) return
    // Need at least an event_type to make a meaningful concept match.
    if (!form.event_type.trim()) return

    setSuggestRequested(true)
    setSuggesting(true)
    try {
      const result = await onSuggestUnit({
        event_type:  form.event_type.trim(),
        event_date:  form.event_date || null,
        guest_count: form.headcount ? Number(form.headcount) : null,
        notes:       form.notes.trim() || null,
      })
      setSuggestion(result || null)
      // Pre-select the suggested unit, but ONLY if the operator hasn't
      // already picked one manually while the call was in flight.
      const sid = result?.suggested_unit_id
      if (sid && units.some((u) => u.id === sid)) {
        setAssignedUnitId((prev) => (prev ? prev : sid))
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[CateringInquiryModal] unit suggestion failed', e)
      // Allow a retry on the next blur — the dropdown stays manual.
      setSuggestRequested(false)
    } finally {
      // Own in-flight flag resets here — separate from submit lifecycle.
      setSuggesting(false)
    }
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (submitting) return

    if (!form.contact_name.trim()) {
      setError('Contact name is required.')
      return
    }
    if (form.headcount && !Number.isFinite(Number(form.headcount))) {
      setError('Headcount must be a number.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      if (isCreateMode) {
        // Group-create: attach the operator's (possibly AI-suggested,
        // possibly overridden) unit assignment. Single-unit create
        // passes `form` untouched → byte-identical to before this ship.
        if (assignmentMode) {
          await onCreate({ ...form, assigned_restaurant_id: assignedUnitId || null })
        } else {
          await onCreate(form)
        }
      } else {
        await onSave(inquiry.id, {
          contact_name:    form.contact_name.trim(),
          contact_email:   form.contact_email.trim() || null,
          contact_phone:   form.contact_phone.trim() || null,
          event_date:      form.event_date || null,
          event_time:      form.event_time || null,
          headcount:       form.headcount ? Number(form.headcount) : null,
          location:        form.location.trim() || null,
          event_type:      form.event_type.trim() || null,
          notes:           form.notes.trim() || null,
          estimated_total: form.estimated_total ? Number(form.estimated_total) : null,
          status:          form.status,
        })
      }
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[CateringInquiryModal] save failed', e2)
      setError('Could not save. Check your connection and try again.')
    } finally {
      // Always reset — modal stays mounted between opens.
      setSubmitting(false)
    }
  }

  // Status transition handler — flips the inquiry status and calls
  // onSave with just { status }. useCateringData.updateInquiry
  // auto-stamps quoted_at / confirmed_at / completed_at on the flip.
  async function handleStatusTransition(newStatus) {
    if (submitting || !inquiry) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave(inquiry.id, { status: newStatus })
      // Reflect in local form so the select shows the new value
      // without needing to close + reopen.
      setForm((prev) => ({ ...prev, status: newStatus }))
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CateringInquiryModal] status transition failed', e)
      setError(`Could not change status to "${newStatus}". Try again.`)
    } finally {
      // Always reset — modal stays mounted between opens.
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    if (submitting) return
    const ok = window.confirm(
      `Delete this inquiry?\n\n${inquiry.contact_name}${inquiry.event_date ? ' · ' + inquiry.event_date : ''}\n\nThis cannot be undone.`
    )
    if (!ok) return
    setSubmitting(true)
    try {
      await onDelete?.(inquiry.id)
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CateringInquiryModal] delete failed', e)
      setError('Could not delete. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={isCreateMode ? 'New catering inquiry' : `Edit ${inquiry.contact_name}`}
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium truncate">
              {isCreateMode
                ? 'New catering inquiry'
                : `Edit · ${inquiry.contact_name}`}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* Customer */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="cat-name">
              Contact name *
            </label>
            <input
              id="cat-name"
              type="text"
              required
              value={form.contact_name}
              onChange={(e) => set('contact_name', e.target.value)}
              placeholder="Sarah Johnson"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cat-email">
                Email
              </label>
              <input
                id="cat-email"
                type="email"
                inputMode="email"
                value={form.contact_email}
                onChange={(e) => set('contact_email', e.target.value)}
                placeholder="sarah@example.com"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cat-phone">
                Phone
              </label>
              <input
                id="cat-phone"
                type="tel"
                inputMode="tel"
                value={form.contact_phone}
                onChange={(e) => set('contact_phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
          </div>

          {/* Event */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cat-date">
                Event date
              </label>
              <input
                id="cat-date"
                type="date"
                value={form.event_date}
                onChange={(e) => set('event_date', e.target.value)}
                onBlur={maybeSuggestUnit}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cat-time">
                Event time
              </label>
              <input
                id="cat-time"
                type="time"
                value={form.event_time}
                onChange={(e) => set('event_time', e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cat-headcount">
                Headcount
              </label>
              <input
                id="cat-headcount"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={form.headcount}
                onChange={(e) => set('headcount', e.target.value)}
                onBlur={maybeSuggestUnit}
                placeholder="25"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="cat-type">
                Event type
              </label>
              <input
                id="cat-type"
                type="text"
                value={form.event_type}
                onChange={(e) => set('event_type', e.target.value)}
                onBlur={maybeSuggestUnit}
                placeholder="corporate / wedding / birthday / etc."
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
          </div>

          {/* ---------- Unit assignment (group-create mode only) -------- */}
          {/* Editable dropdown pre-selected to the AI suggestion. A small
              inline indicator (sparkle, NOT a blue spinner, NOT the full
              KitchenLoader) shows while the suggest call is in flight. A
              "Why?" expander reveals the model's free-text reasoning. No
              cost/model text anywhere. */}
          {assignmentMode && (
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="block text-sm font-medium" htmlFor="cat-unit">
                  Assign to location
                </label>
                {suggesting && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-brand-800">
                    <span className="animate-pulse" aria-hidden="true">✨</span>
                    Suggesting best unit…
                  </span>
                )}
              </div>
              <select
                id="cat-unit"
                value={assignedUnitId}
                onChange={(e) => setAssignedUnitId(e.target.value)}
                className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              >
                <option value="">Choose a location…</option>
                {units.map((u) => {
                  const isSuggested = suggestion?.suggested_unit_id === u.id
                  return (
                    <option key={u.id} value={u.id}>
                      {isSuggested ? `✨ ${u.name} (suggested)` : u.name}
                    </option>
                  )
                })}
              </select>
              {suggestion?.reasoning && (
                <div className="mt-1.5">
                  <button
                    type="button"
                    onClick={() => setShowWhy((s) => !s)}
                    className="text-xs font-medium text-blue-700 hover:text-blue-800"
                    aria-expanded={showWhy}
                  >
                    {showWhy ? 'Hide why' : 'Why this suggestion?'}
                  </button>
                  {showWhy && (
                    <p className="mt-1 rounded-lg bg-amber-brand-50 px-3 py-2 text-xs leading-relaxed text-black/70">
                      {suggestion.reasoning}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="cat-location">
              Location
            </label>
            <input
              id="cat-location"
              type="text"
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="123 Main St / pickup / on-site"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {/* Quote / Notes */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="cat-total">
              Estimated total
            </label>
            <input
              id="cat-total"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={form.estimated_total}
              onChange={(e) => set('estimated_total', e.target.value)}
              placeholder="0.00"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            <p className="mt-1 text-xs text-black/55">
              Your quote to the customer (or final price after the event).
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="cat-notes">
              Notes
            </label>
            <textarea
              id="cat-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Menu items requested, dietary restrictions, deposit paid, etc."
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {/* Status */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="cat-status">
              Status
            </label>
            <select
              id="cat-status"
              value={form.status}
              onChange={(e) => set('status', e.target.value)}
              className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Status transition buttons — edit mode only. The select above is the
              raw control; these are quick-flip affordances that match the
              v2 workflow. */}
          {!isCreateMode && inquiry?.status === 'quoted' && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleStatusTransition('confirmed')}
                disabled={submitting}
                className="min-h-btn rounded-lg bg-emerald-600 px-3 py-3 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                Mark confirmed
              </button>
              <button
                type="button"
                onClick={() => handleStatusTransition('declined')}
                disabled={submitting}
                className="min-h-btn rounded-lg border border-black/15 bg-white px-3 py-3 text-sm font-medium text-black/75 hover:bg-black/5 disabled:opacity-50"
              >
                Mark declined
              </button>
            </div>
          )}

          {!isCreateMode && inquiry?.status === 'confirmed' && (
            <div className="grid grid-cols-2 gap-2">
              {/* "Mark completed" is only enabled once the event date has
                  arrived. Otherwise it's hidden — we don't want managers
                  rubber-stamping a future event as done. */}
              {inquiry.event_date && new Date(inquiry.event_date + 'T00:00:00') <= new Date(new Date().setHours(23, 59, 59, 999)) ? (
                <button
                  type="button"
                  onClick={() => handleStatusTransition('completed')}
                  disabled={submitting}
                  className="min-h-btn rounded-lg bg-emerald-600 px-3 py-3 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                >
                  Mark completed
                </button>
              ) : (
                <div />
              )}
              <button
                type="button"
                onClick={() => handleStatusTransition('cancelled')}
                disabled={submitting}
                className="min-h-btn rounded-lg border border-black/15 bg-white px-3 py-3 text-sm font-medium text-black/75 hover:bg-black/5 disabled:opacity-50"
              >
                Mark cancelled
              </button>
            </div>
          )}

          {/* Per-status detail sections — render INSIDE the modal body so
              they're visible on the existing inquiry detail surface. */}
          {!isCreateMode && inquiry?.status === 'quoted' && (
            <>
              <CateringProposalSection inquiry={inquiry} />
              <CateringFollowupSection inquiry={inquiry} />
            </>
          )}
          {!isCreateMode && inquiry?.status === 'confirmed' && (
            <CateringImpactSection inquiry={inquiry} onJumpToSchedule={onJumpToSchedule} />
          )}

          {!isCreateMode && onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className="w-full rounded-lg border border-red-200 bg-white px-4 py-3 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Delete inquiry
            </button>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="submit"
            disabled={submitting}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Saving…'
              : isCreateMode
                ? 'Create inquiry'
                : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  )
}

function emptyForm() {
  return {
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    event_date: '',
    event_time: '',
    headcount: '',
    location: '',
    event_type: '',
    notes: '',
    estimated_total: '',
    status: 'new',
  }
}
